import React, { useState } from 'react';
import { View, Text, TextInput, StyleSheet, KeyboardAvoidingView, ScrollView, Platform } from 'react-native';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { profileService } from '@/services/profileService';
import Toast from '@/components/Toast';
import PrimaryButton from '@/components/PrimaryButton';
import PageHeader from '@/components/PageHeader';
import { useKeyboardVisible } from '@/hooks/useKeyboardVisible';

export default function ChangeNameScreen() {
  const router = useRouter();
  const { t } = useLanguage();
  const insets = useSafeAreaInsets();
  const keyboardVisible = useKeyboardVisible();
  const { user, userProfile, refreshUserData } = useAuth();
  const [name, setName] = useState(userProfile?.full_name || '');
  const [loading, setLoading] = useState(false);
  const [toastVisible, setToastVisible] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error' | 'info'>('success');

  const showToast = (message: string, type: 'success' | 'error' | 'info' = 'success') => {
    setToastMessage(message);
    setToastType(type);
    setToastVisible(true);
  };

  const hideToast = () => {
    setToastVisible(false);
  };

  const handleClose = () => {
    router.back();
  };

  const handleSave = async () => {
    if (!name.trim()) {
      showToast(t('account.changeName.errorRequired'), 'error');
      return;
    }

    if (!user?.id) {
      showToast(t('account.changeName.errorUserNotFound'), 'error');
      return;
    }

    setLoading(true);

    try {
      const result = await profileService.updateName(name.trim());

      if (!result.success) {
        throw new Error(result.error || 'Failed to update name');
      }

      await refreshUserData();

      // PRD §5.10 — `settings_changed` (`field=name`). Success path only.
      import('@/utils/clarityEvents')
        .then(({ logSettingsChanged }) => logSettingsChanged({ field: 'name' }))
        .catch(() => {});

      showToast(t('account.changeName.successMessage'), 'success');
      
      setTimeout(() => {
        router.back();
      }, 1500);
    } catch (error) {
      console.error('Error updating name:', error);
      showToast(t('account.changeName.errorGeneric'), 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <SafeAreaView style={styles.container} edges={['top']}>
      <PageHeader 
        title={t('account.changeName.title')} 
        onBack={handleClose}
        backgroundColor="#FFFFFF"
      />

      <KeyboardAvoidingView 
        style={styles.keyboardAvoidingView} 
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        keyboardVerticalOffset={Platform.OS === 'ios' ? 0 : 20}
      >
        <ScrollView 
          style={styles.scrollView}
          contentContainerStyle={styles.scrollViewContent}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
        >
          <View style={styles.content}>
            <Text style={styles.description}>
              {t('account.changeName.subtitle')}
            </Text>

            <View style={styles.form}>
              <View style={styles.inputGroup}>
                <Text style={styles.label}>{t('account.changeName.label')}</Text>
                <TextInput
                  style={[styles.input, { color: '#151515' }]}
                  value={name}
                  onChangeText={setName}
                  autoFocus
                  placeholder={t('account.changeName.placeholder')}
                  placeholderTextColor="#A19F97"
                  autoCapitalize="words"
                  autoCorrect={false}
                />
              </View>
            </View>
          </View>
        </ScrollView>

        <View style={[styles.footer, { paddingBottom: keyboardVisible ? 16 : insets.bottom + 24 }]}>
          <PrimaryButton
            title={loading ? t('account.changeName.buttonLoading') : t('account.changeName.button')}
            onPress={handleSave}
            disabled={loading}
          />
        </View>
      </KeyboardAvoidingView>

      <Toast
        visible={toastVisible}
        type={toastType}
        message={toastMessage}
        onHide={hideToast}
        duration={4000}
      />
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  keyboardAvoidingView: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  scrollViewContent: {
    flexGrow: 1,
  },
  content: {
    flex: 1,
    paddingHorizontal: 24,
    paddingTop: 24,
  },
  description: {
    fontSize: 24,
    fontFamily: 'Sentient-Regular',
    color: '#151515',
    lineHeight: 28.8,
    marginBottom: 32,
  },
  form: {
    gap: 24,
  },
  inputGroup: {
    gap: 8,
  },
  label: {
    color: '#151515',
    fontFamily: 'Inter',
    fontWeight: '700',
    fontSize: 14,
    lineHeight: 14,
  },
  input: {
    borderWidth: 1,
    borderColor: '#E7EAED',
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 12,
    fontSize: 16,
    fontFamily: 'Inter_400Regular',
    color: '#151515',
    backgroundColor: '#FFFFFF',
  },
  footer: {
    paddingHorizontal: 24,
    paddingTop: 8,
  },
});
